'use client';

import Decimal from 'decimal.js';
import { useCallback, useMemo } from 'react';
import { useTpSlOrders } from '@/lib/api/order/hooks';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import {
  useTradingBalances,
  useTradingTradeHistory,
} from '@/lib/components/Trading/TradingDataContext';
import { useCoins } from '@/lib/providers/CoinsProvider';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';
import { EventOpenPositionsCard } from './EventOpenPositionsCard';

export const EventOpenPositionsList = () => {
  const selectedOutcome = useSelectedOutcome();
  const { user } = useAuthContext();
  const event = useOrderFormStore.use.event();
  const { coins } = useCoins();
  const userId = user?.id;
  const outcomeId = selectedOutcome?.id ?? '';
  const resolutions = selectedOutcome?.r;

  // Get USDC assetId
  const quoteAssetId = useMemo(() => {
    const usdcCoin = coins.find((coin) => coin.shortName.toUpperCase() === 'USDC');
    return usdcCoin?.id || '';
  }, [coins]);

  // Get YES and NO resolution IDs (eventTokenIds)
  const yesResolution = resolutions?.find((r) => r.isPositive === true);
  const noResolution = resolutions?.find((r) => r.isPositive === false);
  const yesTokenId = yesResolution?.id;
  const noTokenId = noResolution?.id;

  // Determine primary for TP/SL queries
  const yesPrimary = yesResolution ? yesResolution.isPositive === true : undefined;
  const noPrimary = noResolution ? noResolution.isPositive === true : undefined;

  // Get TP/SL orders for YES and NO positions
  const yesTpSlOrders = useTpSlOrders(
    { assetId: yesTokenId || '', status: 'ACTIVE', primary: yesPrimary },
    { enabled: Boolean(yesTokenId) },
  );
  const noTpSlOrders = useTpSlOrders(
    { assetId: noTokenId || '', status: 'ACTIVE', primary: noPrimary },
    { enabled: Boolean(noTokenId) },
  );

  // Get user balances from context (already fetched by TradingDataProvider)
  const balancesData = useTradingBalances();

  // Get trade history from context (already fetched by TradingDataProvider)
  const tradeHistoryData = useTradingTradeHistory(outcomeId);

  // Get current prices from WebSocket
  const livePrices = useOutcomePrices(outcomeId);

  // Calculate current price for YES and NO
  const currentYesPrice = useMemo(() => {
    if (typeof livePrices?.bestAsk === 'number') {
      return livePrices.bestAsk;
    }
    if (typeof livePrices?.bestBid === 'number') {
      return 1 - livePrices.bestBid;
    }
    return yesResolution?.price ?? 0;
  }, [livePrices, yesResolution]);

  const currentNoPrice = useMemo(() => {
    if (typeof livePrices?.bestAsk === 'number') {
      return 1 - livePrices.bestAsk;
    }
    if (typeof livePrices?.bestBid === 'number') {
      return livePrices.bestBid;
    }
    return noResolution?.price ?? 0;
  }, [livePrices, noResolution]);

  // Calculate average purchase price from trade history
  const calculateAveragePurchasePrice = useCallback(
    (assetId: string, shares: number): Decimal => {
      if (!tradeHistoryData?.content || !userId || shares < 0) {
        return new Decimal(0);
      }

      const buyTrades = tradeHistoryData.content.filter(
        (trade) => trade.buyerId === userId && trade.assetId === assetId,
      );

      if (buyTrades.length === 0) {
        return new Decimal(0);
      }

      let totalCost = new Decimal(0);
      let totalQuantity = new Decimal(0);

      for (const trade of buyTrades) {
        const price = new Decimal(trade.price);
        const quantity = new Decimal(trade.quantity);
        totalCost = totalCost.plus(price.mul(quantity));
        totalQuantity = totalQuantity.plus(quantity);
      }

      if (totalQuantity.isZero()) {
        return new Decimal(0);
      }

      return totalCost.dividedBy(totalQuantity);
    },
    [tradeHistoryData, userId],
  );

  // Get positions data
  const positions = useMemo(() => {
    if (!balancesData?.balances || !resolutions) return [];

    const positionsList: Array<{
      id: string;
      outcome: 'Yes' | 'No';
      shares: number;
      openPrice: string;
      value: string;
      cost: string;
      /** Raw figures for the share dialog — never parse them back out of the strings above. */
      costValue: number;
      openPriceCents: number;
      pnl: { amount: string; percent: string };
      tpSl?: {
        ordersCount: number;
        takeProfitPrice?: number;
        stopLossPrice?: number;
        totalTpSize?: number;
        totalSlSize?: number;
      } | null;
      eventTokenId: string;
      totalShares?: number;
    }> = [];

    // Process YES position
    if (yesTokenId) {
      const yesBalance = balancesData.balances.find((b) => b.eventTokenId === yesTokenId);
      const yesTotalVolume = yesBalance?.totalVolume ?? 0;
      const yesAvailableShares = yesBalance?.value ?? 0;

      if (yesBalance && yesTotalVolume > 0) {
        // Use totalVolume for calculations (full position), but show availableShares in card
        const totalShares = yesTotalVolume;
        const availableShares = yesAvailableShares;
        // Use averageOpenPrice from API if available, otherwise calculate from trade history
        const avgPurchasePrice =
          yesBalance.averageOpenPrice !== undefined
            ? new Decimal(yesBalance.averageOpenPrice)
            : calculateAveragePurchasePrice(yesTokenId, totalShares);
        // Cost = use "cost" field from API as is, fallback to calculation if not available
        const cost =
          yesBalance.cost !== undefined
            ? new Decimal(yesBalance.cost)
            : avgPurchasePrice.mul(availableShares);
        // Value = open price × cost
        const value = avgPurchasePrice.mul(cost);
        const pnl = value.minus(cost);
        const pnlPercent = cost.isZero()
          ? new Decimal(0)
          : value.dividedBy(cost).mul(100).minus(100);

        // Calculate TP/SL display data
        const yesTpSlData = yesTpSlOrders.data || [];
        let yesTpSlDisplay = null;
        if (yesTpSlData.length > 0) {
          if (yesTpSlData.length === 1) {
            // Single order: show prices
            yesTpSlDisplay = {
              ordersCount: 1,
              takeProfitPrice: yesTpSlData[0].takeProfitPrice,
              stopLossPrice: yesTpSlData[0].stopLossPrice,
            };
          } else {
            // Multiple orders: show total sizes
            yesTpSlDisplay = {
              ordersCount: yesTpSlData.length,
              totalTpSize: yesTpSlData.reduce(
                (sum, order) => (order.takeProfitPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
              totalSlSize: yesTpSlData.reduce(
                (sum, order) => (order.stopLossPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
            };
          }
        }

        positionsList.push({
          id: `yes-${yesTokenId}`,
          outcome: 'Yes',
          shares: yesAvailableShares, // Show available shares
          openPrice: avgPurchasePrice.isZero() ? '0¢' : formatCents(avgPurchasePrice.mul(100)),
          value: formatMoney(value.toNumber(), { prefix: '$' }),
          cost: formatMoney(cost.toNumber(), { prefix: '$' }),
          costValue: cost.toNumber(),
          openPriceCents: avgPurchasePrice.mul(100).toNumber(),
          pnl: {
            amount: formatMoney(pnl.toNumber(), { prefix: '$' }),
            percent: `${formatMoney(pnlPercent.toNumber(), { decimals: 2 })}%`,
          },
          tpSl: yesTpSlDisplay,
          eventTokenId: yesTokenId,
          totalShares: totalShares,
        });
      }
    }

    // Process NO position
    if (noTokenId) {
      const noBalance = balancesData.balances.find((b) => b.eventTokenId === noTokenId);
      const noTotalVolume = noBalance?.totalVolume ?? 0;
      const noAvailableShares = noBalance?.value ?? 0;

      if (noBalance && noTotalVolume > 0) {
        // Use totalVolume for calculations (full position), but show availableShares in card
        const totalShares = noTotalVolume;
        const availableShares = noAvailableShares;
        // Use averageOpenPrice from API if available, otherwise calculate from trade history
        const avgPurchasePrice =
          noBalance.averageOpenPrice !== undefined
            ? new Decimal(noBalance.averageOpenPrice)
            : calculateAveragePurchasePrice(noTokenId, totalShares);
        // Cost = use "cost" field from API as is, fallback to calculation if not available
        const cost =
          noBalance.cost !== undefined
            ? new Decimal(noBalance.cost)
            : avgPurchasePrice.mul(availableShares);
        // Value = open price × cost
        const value = avgPurchasePrice.mul(cost);
        const pnl = value.minus(cost);
        const pnlPercent = cost.isZero()
          ? new Decimal(0)
          : value.dividedBy(cost).mul(100).minus(100);

        // Calculate TP/SL display data
        const noTpSlData = noTpSlOrders.data || [];
        let noTpSlDisplay = null;
        if (noTpSlData.length > 0) {
          if (noTpSlData.length === 1) {
            // Single order: show prices
            noTpSlDisplay = {
              ordersCount: 1,
              takeProfitPrice: noTpSlData[0].takeProfitPrice,
              stopLossPrice: noTpSlData[0].stopLossPrice,
            };
          } else {
            // Multiple orders: show total sizes
            noTpSlDisplay = {
              ordersCount: noTpSlData.length,
              totalTpSize: noTpSlData.reduce(
                (sum, order) => (order.takeProfitPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
              totalSlSize: noTpSlData.reduce(
                (sum, order) => (order.stopLossPrice > 0 ? sum + order.quantity : sum),
                0,
              ),
            };
          }
        }

        positionsList.push({
          id: `no-${noTokenId}`,
          outcome: 'No',
          shares: noAvailableShares, // Show available shares
          openPrice: avgPurchasePrice.isZero() ? '0¢' : formatCents(avgPurchasePrice.mul(100)),
          value: formatMoney(value.toNumber(), { prefix: '$' }),
          cost: formatMoney(cost.toNumber(), { prefix: '$' }),
          costValue: cost.toNumber(),
          openPriceCents: avgPurchasePrice.mul(100).toNumber(),
          pnl: {
            amount: formatMoney(pnl.toNumber(), { prefix: '$' }),
            percent: `${formatMoney(pnlPercent.toNumber(), { decimals: 2 })}%`,
          },
          tpSl: noTpSlDisplay,
          eventTokenId: noTokenId,
          totalShares: totalShares,
        });
      }
    }

    return positionsList;
  }, [
    balancesData,
    resolutions,
    yesTokenId,
    noTokenId,
    calculateAveragePurchasePrice,
    yesTpSlOrders.data,
    noTpSlOrders.data,
  ]);

  // Calculate position data for each position
  const getPositionData = useCallback(
    (position: (typeof positions)[number]) => {
      // Get avgOpenPrice from balance or parse from openPrice string
      let avgOpenPrice = 0;
      if (balancesData?.balances) {
        const positionBalance = balancesData.balances.find(
          (b) => b.eventTokenId === position.eventTokenId,
        );
        if (positionBalance?.averageOpenPrice !== undefined) {
          avgOpenPrice = positionBalance.averageOpenPrice;
        } else {
          // Fallback: parse open price from string (e.g., "42¢" -> 0.42)
          const openPriceStr = position.openPrice.replace('¢', '');
          avgOpenPrice = parseFloat(openPriceStr) / 100;
        }
      } else {
        // Fallback: parse open price from string (e.g., "42¢" -> 0.42)
        const openPriceStr = position.openPrice.replace('¢', '');
        avgOpenPrice = parseFloat(openPriceStr) / 100;
      }

      // Get current price based on outcome
      const currentPrice = position.outcome === 'Yes' ? currentYesPrice : currentNoPrice;

      return {
        avgOpenPrice,
        currentPrice,
      };
    },
    [balancesData, currentYesPrice, currentNoPrice],
  );

  if (positions.length === 0) {
    return null;
  }

  if (!event) {
    return null;
  }

  return (
    <>
      {positions.map((position) => {
        const { avgOpenPrice, currentPrice } = getPositionData(position);
        return (
          <EventOpenPositionsCard
            key={position.id}
            position={position}
            eventId={event.id}
            outcomeId={outcomeId}
            quoteAssetId={quoteAssetId}
            avgOpenPrice={avgOpenPrice}
            currentPrice={currentPrice}
            eventTitle={event.title}
            eventImage={event.image}
          />
        );
      })}
    </>
  );
};
